import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    ConstrainMode,
    ICommandBarItemProps,
    IContextualMenuItem,
    IObjectWithKey,
    ScrollablePane,
    ScrollbarVisibility,
    Selection
} from "office-ui-fabric-react";
import {
    ApprovalDecisionType,
    ApprovalRequest,
    ApprovalRequestStatus,
    ApprovalRequestAttrs
} from "../../store/ApprovalCenterStore";
import { EntityType } from "../../entities/common";
import { DecisionPopupComponent, SaveApprovalTabSetting } from "./approvalTabBase";
import { nameof } from "../../store/services/metadataService";
import * as Metadata from "../../entities/Metadata";
import * as FiltersStore from "../../store/filters";
import { FilterHelper, FieldCompareOverride } from "../../store/approvalRequest/filters";
import { PersistDetailsList, OnItemRender, onRenderDetailsHeader } from "../common/extensibleEntity/EntityDetailsList";
import { ListUserSettings } from "../../store/services/viewSaver";
import ApprovalCenterCommandBar from "./ApprovalCenterCommandBar";
import FilterPanel from "../common/FilterPanel";
import { ApprovalStatusBadge } from "./ApprovalStatusBadge";
import { RowMenu } from "../common/extensibleEntity/RowMenu";
import Spinner, { ControlSpiner } from "../common/Spinner";
import { ClearFilterComponent, SectionControlPlaceholder } from "../common/sectionsControl/SectionPlaceholder";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { getClearFilterId } from "../common/withFilter";
import { ConfigureFieldsPanel } from "../field/ConfigureFields";

interface ApprovalActionCreators {
    loadRequests: (status?: ApprovalRequestStatus) => void;
    approveRequests: (requestIds: string[], comment?: string, callback?: () => void, payload?: Record<string, any>) => void;
    rejectRequests: (requestIds: string[], comment?: string, callback?: () => void, payload?: Record<string, any>) => void;
    recallRequests: (requestIds: string[], callback?: () => void) => void;
}

interface Props<TDetails> extends ListUserSettings {
    requests: ApprovalRequest<TDetails>[];
    isLoading: boolean;
    isReadonlyMode?: boolean;
    actions: ApprovalActionCreators;
    serverFields: Metadata.Field[];
    fakeFields: Metadata.Field[];
    filterableFakeFields?: Metadata.Field[];
    displayFields: string[];
    configurableFields: Metadata.Field[];
    mandatoryFields?: string[];
    onDisplayFieldsChange: (fields: string[]) => void;
    filters: Metadata.IFilter<Metadata.BaseFilterValue>[];
    activeFilter?: Metadata.IFilter<Metadata.BaseFilterValue>;
    saveSetting: SaveApprovalTabSetting;
    canManageConfiguration: boolean;
    filtersActions: ReturnType<typeof FiltersStore.actionCreators.forEntity>;
    fieldValueExtractor?: (item: ApprovalRequest<TDetails>, field: Metadata.Field) => any;
    fieldCompareOverride?: FieldCompareOverride<ApprovalRequest<TDetails>>;
    onItemRender?: OnItemRender<ApprovalRequest<TDetails>>;
    children?: React.ReactNode;
    preFilterOptions?: Metadata.PreFilterOption<ApprovalRequest<TDetails>>[];
    preFilterId?: string;
    canRecall?: (item: ApprovalRequest<TDetails>) => boolean;
    recallDialogContent?: (requests: ApprovalRequest<TDetails>[]) => {
        title: string;
        subText: string;
        note?: string;
    };
    decisionPopup: DecisionPopupComponent<TDetails>;
    getSelectionCommands?: (selectedIds: string[], selectedRequests: ApprovalRequest<TDetails>[]) => ICommandBarItemProps[];
    getItemCommands?: (item: ApprovalRequest<TDetails>) => IContextualMenuItem[];
    banner?: JSX.Element;
    commandBarItems?: ICommandBarItemProps[];
}

const useActiveFilterPersist = (saveSetting: SaveApprovalTabSetting, currentPreFilterId: string | undefined) =>
    useCallback(
        (filterId?: string, newPreFilterId?: string | null) => {
            const effectivePreFilterId = newPreFilterId === null ? null : (newPreFilterId ?? currentPreFilterId ?? null);
            saveSetting.activeFilter({
                filterId: filterId ?? null,
                preFilterId: effectivePreFilterId
            });
        },
        [saveSetting, currentPreFilterId]
    );

function ApprovalTabContent<TDetails>(props: Readonly<Props<TDetails>>) {
    const {
        requests,
        isLoading,
        isReadonlyMode,
        actions,
        serverFields,
        fakeFields,
        displayFields,
        onDisplayFieldsChange,
        filters,
        activeFilter,
        saveSetting,
        canManageConfiguration,
        filtersActions,
        fieldValueExtractor,
        fieldCompareOverride,
        onItemRender,
        children,
        controlSettings,
        onSaveSettings,
        preFilterOptions,
        preFilterId,
        canRecall,
        recallDialogContent,
        decisionPopup: DecisionPopup,
        getSelectionCommands,
        getItemCommands: getCustomItemCommands
    } = props;

    const handleActiveFilterChange = useActiveFilterPersist(saveSetting, preFilterId);

    const preFilter = useMemo(
        () =>
            preFilterOptions && preFilterOptions.length > 0
                ? Metadata.PreFilter.create(preFilterOptions, active =>
                      handleActiveFilterChange(activeFilter?.id, active?.key ?? null)
                  )
                : undefined,
        [preFilterOptions, handleActiveFilterChange, activeFilter]
    );

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [actionRequestIds, setActionRequestIds] = useState<string[]>([]);
    const [decisionDirection, setDecisionDirection] = useState<ApprovalDecisionType | null>(null);
    const [recallDialogOpen, setRecallDialogOpen] = useState(false);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [isConfigureColumnsOpen, setIsConfigureColumnsOpen] = useState(false);

    // canRecall changes in delegation mode, but Selection must be created once - so read it via ref
    const canRecallRef = useRef(canRecall);
    canRecallRef.current = canRecall;

    const selection = useMemo(() => {
        const sel = new Selection({
            onSelectionChanged: () => {
                setSelectedIds((sel.getSelection() as ApprovalRequest[]).map(_ => _.id));
            },
            getKey: (_: IObjectWithKey) => (_ as ApprovalRequest).id,
            canSelectItem: item => {
                const request = item as ApprovalRequest<TDetails>;
                return request.attributes.Status === ApprovalRequestStatus.Pending || !!canRecallRef.current?.(request);
            }
        });
        return sel;
    }, []);

    const selectedRequests = useMemo(() => requests.filter(r => selectedIds.includes(r.id)), [requests, selectedIds]);

    const allSelectedPending =
        selectedIds.length > 0 && selectedRequests.every(r => r.attributes.Status === ApprovalRequestStatus.Pending);
    const canRecallSelected = selectedIds.length > 0 && selectedRequests.every(r => !!canRecall?.(r));
    const selectionCommands = useMemo(
        () => getSelectionCommands?.(selectedIds, selectedRequests) ?? [],
        [getSelectionCommands, selectedIds, selectedRequests]
    );

    const handleApprove = useCallback(() => {
        setActionRequestIds(selectedIds);
        setDecisionDirection(ApprovalDecisionType.Approved);
    }, [selectedIds]);
    const handleReject = useCallback(() => {
        setActionRequestIds(selectedIds);
        setDecisionDirection(ApprovalDecisionType.Rejected);
    }, [selectedIds]);
    const handleRecall = useCallback(() => {
        setActionRequestIds(selectedIds);
        setRecallDialogOpen(true);
    }, [selectedIds]);

    useEffect(() => {
        actions.loadRequests();
    }, []);

    useEffect(() => {
        const requestIds = new Set(requests.map(r => r.id));
        setSelectedIds(prev => {
            const pruned = prev.filter(id => requestIds.has(id));
            return pruned.length === prev.length ? prev : pruned;
        });
    }, [requests]);

    const { filterableFakeFields } = props;

    const allFields = useMemo(() => [...serverFields, ...fakeFields], [serverFields, fakeFields]);
    const filterFields = useMemo(
        () => [...serverFields, ...(filterableFakeFields ?? [])],
        [serverFields, filterableFakeFields]
    );

    const filterHelper = useMemo(
        () => new FilterHelper({ fields: allFields, fieldValueExtractor, fieldCompareOverride }),
        [allFields, fieldValueExtractor, fieldCompareOverride]
    );

    const compatibleFilters = useMemo(() => {
        const known = new Set(filterFields.map(f => f.name));
        return filters.filter(
            f => f.isBuiltIn || !f.attributeNames?.length || f.attributeNames.every(a => known.has(a.name))
        );
    }, [filters, filterFields]);

    const filteredRequests = useMemo(() => {
        let result = requests;
        if (preFilter) {
            result = result.filter(item => !Metadata.PreFilter.isItemVisible(preFilter, item, preFilterId));
        }
        return filterHelper.filter(result, activeFilter);
    }, [filterHelper, requests, activeFilter, preFilter, preFilterId]);

    const onClearFilter = useCallback(() => {
        const clearFilterId = getClearFilterId(EntityType.ApprovalRequest, compatibleFilters);
        if (clearFilterId) {
            handleActiveFilterChange(clearFilterId, clearFilterId);
        }
    }, [compatibleFilters, handleActiveFilterChange]);

    const handleFilterAdd = useCallback(() => {
        const newFilter = filterHelper.newFilter("New Filter").build();
        filtersActions.updateFilter(newFilter);
        handleActiveFilterChange(newFilter.id);
        setIsFilterPanelOpen(true);
    }, [filterHelper, filtersActions, handleActiveFilterChange]);

    const handleFilterEdit = useCallback(
        (id: string) => {
            if (id !== activeFilter?.id) {
                handleActiveFilterChange(id);
            }
            setIsFilterPanelOpen(true);
        },
        [activeFilter, handleActiveFilterChange]
    );

    const handleFilterRemove = useCallback(
        (id: string) => {
            const autoFilterId = Metadata.Filter.getAutoFilterId(filters);
            if (autoFilterId) {
                handleActiveFilterChange(autoFilterId);
            }
            filtersActions.removeFilter(id);
        },
        [filters, handleActiveFilterChange, filtersActions]
    );

    const handleFilterCopy = useCallback(
        (filter: Metadata.IFilter<Metadata.BaseFilterValue>) => {
            setIsFilterPanelOpen(true);
            filtersActions.saveFilter(Metadata.Filter.copy(filter), newFilter => handleActiveFilterChange(newFilter.id));
        },
        [filtersActions, handleActiveFilterChange]
    );

    const getItemCommands = useCallback(
        (item: ApprovalRequest<TDetails>): IContextualMenuItem[] => {
            if (isReadonlyMode) {
                return [];
            }
            const customCommands = getCustomItemCommands?.(item) ?? [];
            if (item.attributes.Status === ApprovalRequestStatus.Pending) {
                return [
                    {
                        key: "approve",
                        name: "Approve",
                        iconProps: { iconName: "Completed" },
                        onClick: () => {
                            setActionRequestIds([item.id]);
                            setDecisionDirection(ApprovalDecisionType.Approved);
                        }
                    },
                    {
                        key: "reject",
                        name: "Reject",
                        iconProps: { iconName: "Broom" },
                        onClick: () => {
                            setActionRequestIds([item.id]);
                            setDecisionDirection(ApprovalDecisionType.Rejected);
                        }
                    },
                    ...customCommands
                ];
            }
            if (canRecall?.(item)) {
                return [
                    {
                        key: "recall",
                        name: "Recall",
                        iconProps: { iconName: "Undo" },
                        onClick: () => {
                            setActionRequestIds([item.id]);
                            setRecallDialogOpen(true);
                        }
                    },
                    ...customCommands
                ];
            }
            return customCommands;
        },
        [isReadonlyMode, canRecall, getCustomItemCommands]
    );

    const onItemMenuRender = useCallback(
        (item: ApprovalRequest<TDetails>) => {
            const commands = getItemCommands(item);
            if (!commands.length) {
                return null;
            }
            return (
                <div className="menu">
                    <RowMenu item={item} commands={commands} />
                </div>
            );
        },
        [getItemCommands]
    );

    const wrappedOnItemRender = useCallback(
        (entity: any, index: number, field: Metadata.Field, defaultRender: () => JSX.Element) => {
            if (field.name === nameof<ApprovalRequestAttrs>("Status")) {
                const renderStatusBadge = () => <ApprovalStatusBadge status={entity.attributes.Status} />;
                return onItemRender ? onItemRender(entity, index, field, renderStatusBadge) : renderStatusBadge();
            }
            if (onItemRender) {
                return onItemRender(entity, index, field, defaultRender);
            }
            return defaultRender();
        },
        [onItemRender]
    );

    return (
        <>
            <div className="entities-list-header">
                <ApprovalCenterCommandBar
                    selectedCount={selectedIds.length}
                    allSelectedPending={allSelectedPending}
                    canRecallSelected={canRecallSelected}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onRecall={handleRecall}
                    selectionCommands={selectionCommands}
                    onCancelSelection={() => selection.setAllSelected(false)}
                    filters={compatibleFilters}
                    activeFilter={activeFilter}
                    onFilterChange={handleActiveFilterChange}
                    canManageConfiguration={canManageConfiguration}
                    onFilterAddClick={handleFilterAdd}
                    onFilterEditClick={handleFilterEdit}
                    onFilterRemoveClick={handleFilterRemove}
                    onFilterCopyClick={handleFilterCopy}
                    preFilter={preFilter}
                    preFilterId={preFilterId}
                    isReadonlyMode={isReadonlyMode}
                    onConfigureColumnsClick={() => setIsConfigureColumnsOpen(true)}
                    extraItems={props.commandBarItems}
                />
            </div>
            {props.banner}
            {isConfigureColumnsOpen && (
                <ConfigureFieldsPanel
                    onDismiss={() => setIsConfigureColumnsOpen(false)}
                    fields={props.configurableFields}
                    selected={displayFields}
                    entityType={EntityType.ApprovalRequest}
                    onChange={onDisplayFieldsChange}
                    mandatoryFields={props.mandatoryFields}
                />
            )}
            {isFilterPanelOpen && activeFilter && (
                <FilterPanel
                    filter={activeFilter}
                    onFilterChanged={filtersActions.updateFilter}
                    onSave={() => filtersActions.saveFilter(activeFilter, _ => handleActiveFilterChange(_.id))}
                    onCopy={_ => filtersActions.saveFilter(_, newFilter => handleActiveFilterChange(newFilter.id))}
                    onDismiss={() => setIsFilterPanelOpen(false)}
                    canManageConfiguration={canManageConfiguration}
                    fields={filterFields}
                    entityFilterHelper={filterHelper}
                />
            )}
            <ControlSpiner isLoading={isLoading && !!requests.length} className="show-over">
                <div className="entities-list-body list-container">
                    {requests.length
                        ? <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                            <ClearFilterComponent
                                items={requests}
                                filteredItems={filteredRequests}
                                onClearFilter={onClearFilter}>
                                <PersistDetailsList
                                    entities={filteredRequests}
                                    entityType={EntityType.ApprovalRequest}
                                    fields={allFields}
                                    displayFields={displayFields}
                                    selection={selection}
                                    isFieldFake={() => false} //sort by all fields
                                    fieldValueExtractor={fieldValueExtractor}
                                    onItemRender={wrappedOnItemRender}
                                    onItemMenuRender={onItemMenuRender}
                                    controlSettings={controlSettings}
                                    onSaveSettings={onSaveSettings}
                                    fabricListProps={{
                                        constrainMode: ConstrainMode.unconstrained,
                                        onRenderDetailsHeader: onRenderDetailsHeader
                                    }}
                                />
                            </ClearFilterComponent>
                        </ScrollablePane>
                        : isLoading
                            ? <Spinner />
                            : <SectionControlPlaceholder
                                title="No approval requests"
                                description="There are no approval requests to display."
                            />
                    }
                </div>
            </ControlSpiner>
            {children}
            {decisionDirection !== null &&
                actionRequestIds.length > 0 &&
                (() => {
                    const actionRequests = requests.filter(r => actionRequestIds.includes(r.id));
                    if (actionRequests.length === 0) {
                        return null;
                    }
                    const onCancel = () => {
                        setDecisionDirection(null);
                        setActionRequestIds([]);
                    };
                    const onSubmit = (comment: string, payload?: Record<string, any>) => {
                        const callback = () => selection.setAllSelected(false);
                        if (decisionDirection === ApprovalDecisionType.Approved) {
                            actions.approveRequests(actionRequestIds, comment, callback, payload);
                        } else {
                            actions.rejectRequests(actionRequestIds, comment, callback, payload);
                        }
                        onCancel();
                    };
                    return (
                        <DecisionPopup
                            requests={actionRequests}
                            initialDirection={decisionDirection}
                            onSubmit={onSubmit}
                            onCancel={onCancel}
                        />
                    );
                })()}
            {recallDialogOpen && (
                <RecallDialog
                    requests={requests}
                    actionRequestIds={actionRequestIds}
                    recallDialogContent={recallDialogContent}
                    onDismiss={() => setRecallDialogOpen(false)}
                    onRecall={() => {
                        actions.recallRequests(actionRequestIds, () => {
                            selection.setAllSelected(false);
                        });
                    }}
                />
            )}
        </>
    );
}

interface RecallDialogProps<TDetails> {
    requests: ApprovalRequest<TDetails>[];
    actionRequestIds: string[];
    recallDialogContent?: (requests: ApprovalRequest<TDetails>[]) => { title: string; subText: string; note?: string };
    onDismiss: () => void;
    onRecall: () => void;
}

function RecallDialog<TDetails>(props: Readonly<RecallDialogProps<TDetails>>) {
    const { requests, actionRequestIds, recallDialogContent, onDismiss, onRecall } = props;

    const content = useMemo(() => {
        const actionRequests = requests.filter(r => actionRequestIds.includes(r.id));
        return recallDialogContent?.(actionRequests);
    }, [requests, actionRequestIds, recallDialogContent]);

    return (
        <ConfirmationDialog
            onDismiss={onDismiss}
            onYes={onRecall}
            onNo={() => { }}
            dialogContentProps={{
                title: content?.title ?? "Recall Approval",
                subText: content?.subText ?? "Are you sure you want to recall the approval?"
            }}
            yesButtonProps={{ text: "Recall" }}
            noButtonProps={{ text: "Cancel" }}>
            {content?.note && <div style={{ fontSize: 12, color: "#605e5c", marginTop: 8 }}>{content.note}</div>}
        </ConfirmationDialog>
    );
}

export default ApprovalTabContent;
